/**
 * WHOLE-CARD CLICK TARGETS
 * ========================
 *
 * Cards that represent a single record (a decision, a review) navigate when
 * clicked anywhere, not only on their title.
 */

import type { MouseEvent } from 'react';

/**
 * Descendants that handle their own clicks. A click on one of these must not
 * also activate the card: the Edit and Delete buttons inside a decision card
 * would otherwise navigate away as well.
 *
 * `[data-card-ignore]` is the escape hatch for anything interactive that is
 * not one of these elements, such as a Badge with its own onClick.
 */
const INTERACTIVE_SELECTOR =
  'a[href], button, input, select, textarea, label, [role="button"], [role="menuitem"], [role="checkbox"], [data-card-ignore]';

/**
 * Props that make a whole card activate `onActivate`, matching what clicking
 * its title did before.
 *
 * Deliberately no `role="button"`/`tabIndex`: these cards contain buttons, and
 * a focusable button-role container wrapping buttons is invalid nesting for
 * assistive technology. Keyboard users reach the same destination through the
 * card's own View button, as they did when only the title was clickable.
 */
export function clickableCardProps(onActivate: () => void) {
  return {
    onClick: (event: MouseEvent<HTMLElement>) => {
      const target = event.target as Element | null;
      if (target?.closest(INTERACTIVE_SELECTOR)) return;
      onActivate();
    },
  };
}
